import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Clock, ArrowRight, FlaskConical, Tag as TagIcon, Folder } from "lucide-react";

function ArticleCard({ a, featured = false }) {
  return (
    <Link to={`/research/${a.slug}`} data-testid={`article-card-${a.slug}`} className={`block border border-slate-200 rounded-md overflow-hidden bg-white hover:border-blue-400 transition-colors ${featured ? "md:col-span-2 lg:grid lg:grid-cols-2 lg:gap-0" : ""}`}>
      <div className={`bg-slate-50 border-b border-slate-100 lg:border-b-0 lg:border-r ${featured ? "aspect-video lg:aspect-auto" : "aspect-[16/10]"} overflow-hidden`}>
        {a.featured_image ? <img src={a.featured_image} alt="" className="w-full h-full object-cover"/> :
          <div className="w-full h-full flex items-center justify-center"><FlaskConical className="w-10 h-10 text-blue-600/70" strokeWidth={1.25}/></div>}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          {a.category_id && <Badge className="rounded-md font-mono text-[10px] uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-200">{a.category_id}</Badge>}
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 inline-flex items-center gap-1"><Clock className="w-3 h-3"/> {a.reading_time || 1} min</span>
        </div>
        <div className={`font-display font-semibold text-slate-900 tracking-tight ${featured ? "text-2xl" : "text-lg"}`}>{a.title}</div>
        {a.excerpt && <p className="mt-2 text-sm text-slate-600 line-clamp-3">{a.excerpt}</p>}
        <div className="mt-3 flex items-center text-blue-700 text-xs font-medium">Read article <ArrowRight className="w-3.5 h-3.5 ml-1"/></div>
      </div>
    </Link>
  );
}

export default function ResearchHub() {
  const [hub, setHub] = useState(null);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const tag = params.get("tag") || "";
  const sort = params.get("sort") || "newest";
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const isFiltering = !!(q || category || tag || sort !== "newest");

  useEffect(() => {
    api.get("/research/hub").then((r) => setHub(r.data)).catch(() => setHub({}));
  }, []);

  useEffect(() => {
    if (!isFiltering) { setResults([]); return; }
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q); if (category) p.set("category", category); if (tag) p.set("tag", tag); if (sort) p.set("sort", sort);
    api.get(`/research?${p}`).then((r) => setResults(r.data)).finally(() => setLoading(false));
  }, [q, category, tag, sort, isFiltering]);

  const update = (patch) => {
    const p = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => v ? p.set(k, v) : p.delete(k));
    setParams(p);
  };

  if (!hub) return <div className="max-w-6xl mx-auto px-6 py-16 text-slate-400 text-sm">Loading Research Hub…</div>;

  return (
    <div data-testid="research-hub-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <div className="mb-8">
        <div className="eyebrow mb-2">Research Hub · Knowledge base</div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold text-slate-900 tracking-tight leading-[1.05]">Peer-reviewed. <span className="text-blue-600">Peptide-first.</span></h1>
        <p className="mt-4 max-w-2xl text-slate-600">Compound guides, protocols, and study summaries curated by our in-house research team.</p>
      </div>

      <div className="mb-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input value={q} onChange={(e) => update({ q: e.target.value || "" })} placeholder="Search articles…" className="h-10 pl-9" data-testid="research-search"/>
        </div>
        <select value={category} onChange={(e) => update({ category: e.target.value })} data-testid="research-category-filter" className="h-10 px-3 rounded-md border border-slate-300 text-sm">
          <option value="">All categories</option>
          {hub.categories?.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <select value={sort} onChange={(e) => update({ sort: e.target.value })} data-testid="research-sort" className="h-10 px-3 rounded-md border border-slate-300 text-sm">
          <option value="newest">Newest</option>
          <option value="popular">Most viewed</option>
          <option value="alphabetical">A – Z</option>
        </select>
      </div>

      {isFiltering ? (
        <section>
          <h2 className="font-display text-xl font-semibold mb-4">{loading ? "Searching…" : `${results.length} result${results.length === 1 ? "" : "s"}`}</h2>
          {results.length === 0 && !loading ? <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded p-10 text-center">No articles match your filters.</div> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.map((a) => <ArticleCard key={a.slug} a={a}/>)}
            </div>
          )}
        </section>
      ) : (
        <>
          {hub.featured && (
            <section className="mb-10">
              <h2 className="eyebrow mb-3">Featured</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4"><ArticleCard a={hub.featured} featured/></div>
            </section>
          )}
          {hub.newest?.length > 0 && (
            <section className="mb-10">
              <h2 className="font-display text-2xl font-semibold mb-4">Newest research</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{hub.newest.map((a) => <ArticleCard key={a.slug} a={a}/>)}</div>
            </section>
          )}
          {hub.popular?.length > 0 && (
            <section className="mb-10">
              <h2 className="font-display text-2xl font-semibold mb-4">Popular</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{hub.popular.map((a) => <ArticleCard key={a.slug} a={a}/>)}</div>
            </section>
          )}
          <section className="mb-10">
            <h2 className="font-display text-2xl font-semibold mb-4">Browse by category</h2>
            <div className="flex flex-wrap gap-2">
              {hub.categories?.map((c) => (
                <Link key={c.slug} to={`/research/category/${c.slug}`} data-testid={`cat-link-${c.slug}`} className="h-9 px-3 rounded-md border border-slate-200 bg-white hover:border-blue-400 text-sm text-slate-800 inline-flex items-center gap-1.5"><Folder className="w-4 h-4 text-blue-600"/> {c.name}</Link>
              ))}
            </div>
          </section>
          <section className="mb-10">
            <h2 className="font-display text-2xl font-semibold mb-4">Trending topics</h2>
            <div className="flex flex-wrap gap-2">
              {hub.trending_tags?.map((t) => (
                <Link key={t.slug} to={`/research/tag/${t.slug}`} data-testid={`tag-link-${t.slug}`} className="h-8 px-3 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-xs inline-flex items-center gap-1"><TagIcon className="w-3.5 h-3.5"/> {t.name}</Link>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
